import { ChatWidgetText, ConfirmWidget } from "../../../Helper/ChatWidget";
import { AlertIcon, InfoIcon } from "../ChatWidgetIcons";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
  Receipt,
  receiptTime,
} from "../ChatWidgetPrimitives";
import "./ConfirmCard.scss";

/** "Cancellation scheduled · Pro ends 14 Mar 2027" → title + detail. */
const splitReceipt = (label: string) => {
  const index = label.indexOf(" · ");
  return index === -1
    ? { title: label, detail: undefined }
    : { title: label.slice(0, index), detail: label.slice(index + 3) };
};

const ConfirmReceipt = ({
  widget,
  onAnswer,
  text,
}: {
  widget: ConfirmWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const response = widget.response!;
  // The stored action expired before the answer: nothing ran, no Undo.
  if (response.expired || widget.state === "expired") {
    return (
      <Receipt
        tone="neutral"
        title={response.label || text("widgetNoChanges")}
        detail={widget.title}
      />
    );
  }
  // Sent, waiting for the server to run it (billing, commerce): no success
  // tick before anything happened.
  if (response.pending) {
    return (
      <Receipt
        tone="pending"
        title={response.label}
        detail={widget.title}
      />
    );
  }
  if (response.action !== "confirm") {
    return (
      <Receipt
        tone="neutral"
        title={text("widgetNoChanges")}
        detail={response.label}
      />
    );
  }
  const receipt = splitReceipt(
    widget.receiptLabel || response.label || text("widgetConfirmed"),
  );
  if (response.undoneAt) {
    return (
      <Receipt
        tone="neutral"
        title={text("widgetUndone")}
        detail={receipt.title}
        isDetailStruck
      />
    );
  }
  const undoLabel = widget.undoLabel;
  return (
    <Receipt
      title={receipt.title}
      detail={
        receipt.detail ??
        `${text("widgetYouConfirmed")} · ${receiptTime(response.at, text)}`
      }
      action={
        undoLabel
          ? {
              label: undoLabel,
              onClick: () =>
                onAnswer("cancel", {}, undoLabel, { kind: "undo" }),
            }
          : undefined
      }
    />
  );
};

/** 04 Confirm: asked before anything changes for the customer. */
export const ConfirmCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: ConfirmWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  if (widget.response) {
    return <ConfirmReceipt widget={widget} onAnswer={onAnswer} text={text} />;
  }
  const icon = widget.icon ?? (widget.destructive ? "alert" : "info");
  const confirmLabel = widget.confirmLabel || text("widgetConfirm");
  const cancelLabel = widget.cancelLabel || text("widgetCancel");
  return (
    <Card className="cw-confirm" label={widget.title}>
      <div className="cw-pad cw-confirm-head">
        <span
          className={cx(
            "cw-tile",
            icon === "alert" ? "cw-tone--danger" : "cw-tone--info",
          )}
          aria-hidden="true"
        >
          {icon === "alert" ? <AlertIcon size={20} /> : <InfoIcon size={20} />}
        </span>
        <div className="cw-stack cw-grow">
          {widget.title && <span className="cw-h">{widget.title}</span>}
          {widget.subtitle && (
            <span className="cw-sm cw-muted">{widget.subtitle}</span>
          )}
          {widget.description && (
            <span className="cw-sm cw-body cw-confirm-desc">
              {widget.description}
            </span>
          )}
        </div>
      </div>
      {widget.fields && widget.fields.length > 0 && (
        <dl className="cw-confirm-fields">
          {widget.fields.map((field, index) => (
            <div className="cw-row cw-sm" key={index}>
              <dt className="cw-muted">{field.label}</dt>
              <dd className="cw-num">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <Foot flush>
        <Button
          variant="ghost"
          onClick={() => onAnswer("cancel", {}, cancelLabel)}
        >
          {cancelLabel}
        </Button>
        <Button
          variant={widget.destructive ? "danger" : "primary"}
          onClick={() => onAnswer("confirm", {}, confirmLabel)}
        >
          {confirmLabel}
        </Button>
      </Foot>
    </Card>
  );
};
